import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from "@angular/core";
import { UntypedFormGroup } from "@angular/forms";
import { BillingSettings } from "../../../settings/settings.interface";
import { DateFormatService } from "../../../shared/date-format.service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { BillingStats } from "../../billing.interface";

@Component({
  selector: "app-subscription-info-card",
  templateUrl: "./subscription-info-card.component.html",
  styleUrls: ["./subscription-info-card.component.scss"],
  standalone: false,
})
export class SubscriptionInfoCardComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() billingSettings: BillingSettings | null = null;
  @Input() stats: BillingStats | null = null;
  @Input() form: UntypedFormGroup | null = null;

  private destroy$ = new Subject<void>();
  private formListenersSetup = false;

  constructor(
    public dateFormatService: DateFormatService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.setupFormListeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.form && this.form) {
      this.formListenersSetup = false;
      this.setupFormListeners();
    }
  }

  private setupFormListeners(): void {
    if (!this.form || this.formListenersSetup) {
      return;
    }

    this.form
      .get("subscriptionPeriod")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.form
      .get("price")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.form
      .get("currency")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.form
      .get("lastInvoiceUserCount")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.form
      .get("invoiceUserCount")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.form
      .get("planName")
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.cdr.markForCheck();
      });

    this.formListenersSetup = true;
  }

  getSubscriptionPaidUntilDate(): Date | null {
    if (!this.billingSettings?.subscriptionStartDate) {
      return null;
    }

    const subscriptionPeriod = this.form?.get("subscriptionPeriod")
      ?.value as string;
    const subscriptionStartDate = new Date(
      this.billingSettings.subscriptionStartDate,
    );

    if (subscriptionPeriod === "yearly") {
      if (this.stats?.aggregated && this.stats.aggregated.length > 0) {
        const yearlyAggregatedStats = this.stats.aggregated.filter(
          (stat) => stat.subscriptionPeriod === "yearly" && !stat.paymentFailed,
        );

        if (yearlyAggregatedStats.length > 0) {
          const latestStat = yearlyAggregatedStats.reduce((latest, current) =>
            new Date(current.date) > new Date(latest.date) ? current : latest,
          );
          const lastPaymentDate = new Date(latestStat.date);
          const paidUntilFromAggregated = new Date(lastPaymentDate);
          paidUntilFromAggregated.setFullYear(
            paidUntilFromAggregated.getFullYear() + 1,
          );
          return paidUntilFromAggregated;
        }
      }

      if (this.stats?.yearly && this.stats.yearly.length > 0) {
        const latestYearlyStat = this.stats.yearly.reduce((latest, current) =>
          new Date(current.date) > new Date(latest.date) ? current : latest,
        );
        const lastPaymentDate = new Date(latestYearlyStat.date);
        const paidUntilFromYearly = new Date(lastPaymentDate);
        paidUntilFromYearly.setFullYear(paidUntilFromYearly.getFullYear() + 1);
        return paidUntilFromYearly;
      }

      const paidUntilFromStartDate = new Date(subscriptionStartDate);
      paidUntilFromStartDate.setFullYear(
        paidUntilFromStartDate.getFullYear() + 1,
      );
      return paidUntilFromStartDate;
    } else {
      const today = new Date();
      const year = today.getFullYear();
      const month = today.getMonth();
      return new Date(year, month + 1, 0);
    }
  }

  getEstimatedMonthlyCost(): number | null {
    if (!this.billingSettings || !this.form) {
      return null;
    }

    const subscriptionPeriod = this.form.get("subscriptionPeriod")
      ?.value as string;
    if (subscriptionPeriod === "yearly") {
      return null;
    }

    const lastInvoiceUserCount =
      (this.form.get("lastInvoiceUserCount")?.value as number) ??
      this.billingSettings.lastInvoiceUserCount ??
      0;
    const invoiceUserCount =
      (this.form.get("invoiceUserCount")?.value as number) ??
      this.billingSettings.invoiceUserCount ??
      0;
    const maxLicensesFromInvoice = Math.max(
      lastInvoiceUserCount,
      invoiceUserCount,
    );
    const basePrice =
      (this.form.get("price")?.value as number) ??
      this.billingSettings.price ??
      0;
    const isLegacyBilling = this.billingSettings.billingMode === "legacy";

    const effectiveEmployeeCount = isLegacyBilling
      ? maxLicensesFromInvoice
      : Math.max(maxLicensesFromInvoice, 10);
    const fullMonthPrice = parseFloat(
      (basePrice * effectiveEmployeeCount).toFixed(2),
    );

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const subscriptionStartDate = this.billingSettings.subscriptionStartDate
      ? new Date(this.billingSettings.subscriptionStartDate)
      : today;
    subscriptionStartDate.setHours(0, 0, 0, 0);

    const year = subscriptionStartDate.getFullYear();
    const month = subscriptionStartDate.getMonth();
    const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
    const startDay = subscriptionStartDate.getDate();
    const daysRemaining = daysInCurrentMonth - startDay + 1;

    if (
      today.getFullYear() === subscriptionStartDate.getFullYear() &&
      today.getMonth() === subscriptionStartDate.getMonth()
    ) {
      const proportionalMultiplier = daysRemaining / daysInCurrentMonth;
      return parseFloat((fullMonthPrice * proportionalMultiplier).toFixed(2));
    } else {
      return fullMonthPrice;
    }
  }

  getEstimatedYearlyCost(): number | null {
    if (!this.billingSettings || !this.form) {
      return null;
    }

    const subscriptionPeriod = this.form.get("subscriptionPeriod")
      ?.value as string;
    if (subscriptionPeriod !== "yearly") {
      return null;
    }

    const lastInvoiceUserCount =
      (this.form.get("lastInvoiceUserCount")?.value as number) ??
      this.billingSettings.lastInvoiceUserCount ??
      0;
    const invoiceUserCount =
      (this.form.get("invoiceUserCount")?.value as number) ??
      this.billingSettings.invoiceUserCount ??
      0;
    const maxLicensesFromInvoice = Math.max(
      lastInvoiceUserCount,
      invoiceUserCount,
    );
    const basePrice =
      (this.form.get("price")?.value as number) ??
      this.billingSettings.price ??
      0;
    const isLegacyBilling = this.billingSettings.billingMode === "legacy";

    const employeeCount = isLegacyBilling
      ? maxLicensesFromInvoice
      : Math.max(maxLicensesFromInvoice, 10);
    return parseFloat((basePrice * employeeCount * 12).toFixed(2));
  }

  get isLoading(): boolean {
    return !this.billingSettings || !this.form;
  }
}
